import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiClient, ShareLinkResponse } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/stores/authStore";
import { Share2, Copy, Check, ExternalLink, Users, Link as LinkIcon } from "lucide-react";
import { CollaborationPanel } from "./CollaborationPanel";

interface ShareScoreButtonProps {
  projectId: string;
  isOwner?: boolean;
}

export const ShareScoreButton = ({ projectId, isOwner = true }: ShareScoreButtonProps) => {
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [shareLink, setShareLink] = useState<ShareLinkResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCreateShareLink = async () => {
    setLoading(true);
    try {
      const response = await apiClient.createShareLink(projectId);
      setShareLink(response);
      toast({
        title: "Share link created",
        description: "Your project score is now shareable!",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create share link",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;
    
    try {
      await navigator.clipboard.writeText(shareLink.share_url);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Share link copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy link",
        variant: "destructive",
      });
    }
  };

  const handleOpenDialog = async () => {
    setIsOpen(true);
    // Only create share link if user is owner and link doesn't exist
    if (isOwner && !shareLink) {
      await handleCreateShareLink();
    }
  };

  return (
    <>
      <Button
        onClick={handleOpenDialog}
        variant="outline"
        className="gap-2"
      >
        <Share2 className="h-4 w-4" />
        Share & Collaborate
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Share & Collaborate</DialogTitle>
            <DialogDescription>
              Share your score publicly or invite team members to collaborate
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Tabs defaultValue={isOwner ? "share" : "collaborate"} className="w-full">
              {isOwner ? (
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="share" className="gap-2">
                    <LinkIcon className="h-4 w-4" />
                    Public Link
                  </TabsTrigger>
                  <TabsTrigger value="collaborate" className="gap-2">
                    <Users className="h-4 w-4" />
                    Collaborators
                  </TabsTrigger>
                </TabsList>
              ) : (
                <TabsList className="grid w-full grid-cols-1">
                  <TabsTrigger value="collaborate" className="gap-2">
                    <Users className="h-4 w-4" />
                    Collaborators
                  </TabsTrigger>
                </TabsList>
              )}

              {isOwner && (
                <TabsContent value="share" className="space-y-4 mt-4">
                {shareLink ? (
                  <>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          Public Share Link
                        </label>
                        <div className="flex items-center space-x-2 rounded-md border bg-muted p-3">
                          <code className="flex-1 text-sm break-all">
                            {shareLink.share_url}
                          </code>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={handleCopyLink}
                          className="flex-1"
                          variant="default"
                        >
                          {copied ? (
                            <>
                              <Check className="mr-2 h-4 w-4" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="mr-2 h-4 w-4" />
                              Copy Link
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => window.open(shareLink.share_url, "_blank")}
                          variant="outline"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="rounded-lg bg-muted p-4 text-sm space-y-2">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Views:</span>
                          <span className="font-medium">{shareLink.view_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Unique Viewers:</span>
                          <span className="font-medium">{shareLink.unique_viewers.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Signups:</span>
                          <span className="font-medium">{shareLink.signup_conversions}</span>
                        </div>
                      </div>

                      <p className="text-xs text-muted-foreground text-center">
                        Anyone with this link can view a preview. Collaborators get full access.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    Failed to create share link. Please try again.
                  </div>
                )}
                </TabsContent>
              )}

              <TabsContent value="collaborate" className="mt-4">
                <CollaborationPanel
                  projectId={projectId}
                  isOwner={isOwner}
                  currentUserId={user?._id || ''}
                />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};